import React, { useState, useEffect } from 'react';
import { Lightbulb, Plus, Loader, Info } from 'lucide-react';
import innovatechService from '../services/innovatechService';

const SmartSuggestions = ({ 
  hypothesis, 
  mvpDescription, 
  suggestionType, 
  sessionId,
  onAddSuggestion 
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hoveredSuggestion, setHoveredSuggestion] = useState(null);
  const [tooltip, setTooltip] = useState({ show: false, content: null, x: 0, y: 0 });

  const colors = {
    primary: '#10b981',
    primaryLight: '#d1fae5',
    primaryDark: '#065f46',
    secondary: '#8b5cf6',
    accent: '#f59e0b',
    background: '#ffffff',
    surface: '#f8fafc',
    border: '#e2e8f0',
    text: '#1f2937',
    textLight: '#6b7280'
  };

  // Cargar sugerencias al montar el componente
  useEffect(() => {
    if (suggestionType) {
      loadSuggestions();
    }
  }, [hypothesis, suggestionType, mvpDescription]);

  const loadSuggestions = async () => {
    setLoading(true);
    
    console.log('[🔍 Smart Suggestions] Iniciando carga con datos:', {
      hypothesis,
      mvpDescription,
      suggestionType,
      sessionId
    });
    
    try {
      const response = await innovatechService.generateSuggestions({
        hypothesis,
        mvp_description: mvpDescription,
        suggestion_type: suggestionType,
        session_id: sessionId
      });

      console.log('[🔍 Smart Suggestions] Respuesta recibida:', response);

      setSuggestions(response.suggestions || []);
      
      if (response.fallback) {
        console.log('[⚠️ Smart Suggestions] Usando sugerencias de fallback');
      }
    } catch (error) {
      console.error('[❌ Smart Suggestions] Error cargando:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    const suggestionText = suggestion.name || suggestion || '';
    console.log('[🔍 Smart Suggestions] Agregando sugerencia:', suggestionText);
    if (suggestionText.trim()) {
      onAddSuggestion(suggestionText);
    } else {
      console.warn('[⚠️ Smart Suggestions] Sugerencia vacía o inválida:', suggestion);
    }
  };

  const handleMouseEnter = (suggestion, event) => {
    setHoveredSuggestion(suggestion.name);
    setTooltip({
      show: true,
      content: suggestion,
      x: event.clientX,
      y: event.clientY
    });
  };

  const handleMouseLeave = () => {
    setHoveredSuggestion(null);
    setTooltip({ show: false, content: null, x: 0, y: 0 });
  };

  const handleMouseMove = (event) => {
    if (tooltip.show) {
      setTooltip(prev => ({
        ...prev,
        x: event.clientX,
        y: event.clientY
      }));
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'fácil': return colors.primary;
      case 'medio': return colors.accent;
      case 'avanzado': return colors.secondary;
      default: return colors.textLight;
    }
  };

  const getTitle = () => {
    return suggestionType === 'metricas_clave' 
      ? 'Métricas sugeridas por IA:' 
      : 'Métodos sugeridos por IA:';
  };

  if (loading) {
    return (
      <div style={{
        backgroundColor: colors.primaryLight,
        border: `1px solid ${colors.border}`,
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '16px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: colors.primaryDark,
          fontSize: '14px',
          fontWeight: '600'
        }}>
          <Loader className="w-4 h-4 animate-spin" />
          <Lightbulb className="w-4 h-4" />
          Generando sugerencias contextualizadas...
        </div>
      </div>
    );
  }

  return (
    <>
      <div style={{
        backgroundColor: colors.primaryLight,
        border: `1px solid ${colors.border}`,
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '16px',
        position: 'relative'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: colors.primaryDark,
          fontSize: '14px',
          fontWeight: '600',
          marginBottom: '12px'
        }}>
          <Lightbulb className="w-4 h-4" />
          {getTitle()}
        </div>
        
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px'
        }}>
          {suggestions
            .filter(suggestion => suggestion && (suggestion.name || suggestion))
            .slice(0, 8)
            .map((suggestion, index) => (
            <button
              key={index}
              onClick={() => handleSuggestionClick(suggestion)}
              onMouseEnter={(e) => handleMouseEnter(suggestion, e)}
              onMouseLeave={handleMouseLeave}
              onMouseMove={handleMouseMove}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                backgroundColor: hoveredSuggestion === suggestion.name ? colors.primary : colors.background,
                color: hoveredSuggestion === suggestion.name ? '#ffffff' : colors.text,
                border: `1px solid ${colors.border}`,
                borderRadius: '20px',
                padding: '6px 12px',
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: hoveredSuggestion === suggestion.name ? '0 2px 8px rgba(16, 185, 129, 0.2)' : 'none'
              }}
            >
              <Plus className="w-3 h-3" />
              {suggestion.name || suggestion || 'Sugerencia'}
              <div 
                style={{
                  width: '8px',
                  height: '8px',
                  backgroundColor: getDifficultyColor(suggestion.difficulty),
                  borderRadius: '50%',
                  opacity: 0.7
                }}
              />
            </button>
          ))}
        </div>

        {suggestions.length === 0 && !loading && (
          <div style={{
            color: colors.textLight,
            fontSize: '13px',
            fontStyle: 'italic'
          }}>
            No se pudieron generar sugerencias. Verifica tu hipótesis y conexión.
          </div>
        )}
      </div>

      {/* Tooltip */}
      {tooltip.show && tooltip.content && (
        <div
          style={{
            position: 'fixed',
            top: tooltip.y - 10,
            left: Math.max(8, Math.min(tooltip.x + 10, window.innerWidth - 280 - 16)),
            backgroundColor: '#1f2937',
            color: '#ffffff',
            padding: '12px',
            borderRadius: '8px',
            fontSize: '12px',
            maxWidth: Math.min(280, window.innerWidth - 32),
            width: 'auto',
            zIndex: 1000,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            pointerEvents: 'none'
          }}
        >
          <div style={{ fontWeight: '600', marginBottom: '6px', wordWrap: 'break-word' }}>
            {tooltip.content.name}
          </div>
          <div style={{ marginBottom: '6px', lineHeight: '1.4', wordWrap: 'break-word', whiteSpace: 'normal' }}>
            {tooltip.content.description}
          </div>
          <div style={{ 
            fontSize: '11px', 
            color: '#9ca3af',
            borderTop: '1px solid #374151',
            paddingTop: '6px',
            marginTop: '6px',
            lineHeight: '1.4',
            wordWrap: 'break-word',
            whiteSpace: 'normal'
          }}>
            <strong>Por qué es relevante:</strong> {tooltip.content.why_relevant}
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '6px',
            fontSize: '11px'
          }}>
            <span style={{ color: '#9ca3af' }}>
              Dificultad: 
            </span>
            <span style={{ 
              color: getDifficultyColor(tooltip.content.difficulty),
              fontWeight: '600',
              textTransform: 'capitalize'
            }}>
              {tooltip.content.difficulty}
            </span>
          </div>
        </div>
      )}
    </>
  );
};

export default SmartSuggestions;